import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit"
import type { api } from "@/services/api"
import { currentStaffSession, staffSessionActions } from "./staffSessionSlice"
import { advanceStaffEpoch, setStaffAccessUnavailable } from "@/services/staffTransportSession"

/**
 * Cache side of staff teardown. The auth/realtime boundary must also stop its
 * timers/subscriptions and purge draft state before it dispatches a new scope.
 * Public booking state is intentionally not touched.
 */
export function createStaffSessionListener(staffApi: Pick<typeof api, "util">) {
  const listener = createListenerMiddleware()
  listener.startListening({
    actionCreator: staffSessionActions.contextUnavailable,
    effect: () => { setStaffAccessUnavailable(true) },
  })
  listener.startListening({
    matcher: isAnyOf(staffSessionActions.scopeEstablished, staffSessionActions.scopeRevoked, staffSessionActions.contextAccepted),
    effect: (action, context) => {
      setStaffAccessUnavailable(false)
      const before = currentStaffSession(context.getOriginalState())
      const after = currentStaffSession(context.getState())
      // Revocation must also purge pre-migration/unscoped staff cache entries.
      if (!staffSessionActions.scopeRevoked.match(action) &&
        before?.scopeKey === after?.scopeKey && before?.epoch === after?.epoch) return
      advanceStaffEpoch()
      for (const read of context.dispatch(staffApi.util.getRunningQueriesThunk())) read.abort()
      // Reset observation, not the server's mutation. Never replay or report an
      // in-flight old-session command as failed merely because its UI was reset.
      context.dispatch(staffApi.util.resetApiState())
    },
  })
  return listener
}
